import React, { useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';

// Material UI
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';

// Data
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { useSingleFlight } from '../../../lib/hooks/use-single-flight';
import { getMessages } from './session-player-notification-toggle.messages';

// Components
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { FormHelperText } from '../../_core/_ui/forms.component';

export const SET_SESSION_PLAYER_NOTIFICATION = gql`
  mutation SetSessionPlayerNotification($_id: ID!, $data: UpdateAssessmentSessionInput!) {
    updateAssessmentSession(_id: $_id, data: $data) {
      _id
      isPlayerNotificationEnabled
    }
  }
`;

interface ISessionPlayerNotificationToggle {
  assessmentSessionId: string;
  isPlayerNotificationEnabled: boolean;
}

// One stored setting per session; every panel that renders this reads the same cached field.
export const SessionPlayerNotificationToggle: React.FC<ISessionPlayerNotificationToggle> = ({
  assessmentSessionId,
  isPlayerNotificationEnabled,
}) => {
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);
  const [saveFailed, setSaveFailed] = useState(false);

  const [setPlayerNotification] = useMutation(SET_SESSION_PLAYER_NOTIFICATION);

  const saveFlight = useSingleFlight(async (value: boolean) => {
    setSaveFailed(false);
    try {
      await setPlayerNotification({
        variables: { _id: assessmentSessionId, data: { isPlayerNotificationEnabled: value } },
        optimisticResponse: {
          updateAssessmentSession: {
            __typename: 'AssessmentSession',
            _id: assessmentSessionId,
            isPlayerNotificationEnabled: value,
          },
        },
      });
    } catch (error) {
      console.error('setSessionPlayerNotification failed', { assessmentSessionId, value, error });
      setSaveFailed(true);
    }
  });

  return (
    <Box>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={isPlayerNotificationEnabled}
              disabled={saveFlight.inFlight}
              onChange={(event) => saveFlight.run(event.target.checked)}
            />
          }
          label={<IntlMsg {...messages.lblNotify} />}
        />
        <Tooltip title={<IntlMsg {...messages.tipNotify} />}>
          <InfoIcon fontSize="small" />
        </Tooltip>
      </Box>
      {saveFailed && <FormHelperText error><IntlMsg {...messages.errNotSaved} /></FormHelperText>}
    </Box>
  );
};
